import * as React from 'react';
import { FormattedMessage, FormattedDate, useIntl } from 'react-intl';
import { formatDuration } from '../../lib/util';
import {
  CurrentUser_currentSubscription,
  updateProductSubscriptionMutation,
  updateProductSubscriptionMutationVariables,
  currentUserQuery,
  CurrentUser_productRenewals,
  ProductSubscriptionStatus,
  UpdateProductRenewalErrorCode,
  PenaltyStatus,
  disableSubscription,
  disableSubscriptionVariables
} from '../../core-types';
import {
  ProductCard,
  H2,
  Text,
  Link,
  I18NBuy,
  I18NCancel,
  I18NContinue
} from '../../components';
import {
  AnchorButton,
  Box,
  Spinner,
  Flex,
  ButtonAnchor,
  Dialog,
  ButtonGroup,
  Button
} from '@urbaninfrastructure/react-ui-kit';
import * as messages from './messages';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FormattedCurrency,
  formatCurrency
} from '../../components/FormattedCurrency';
import { Warning } from '@urbaninfrastructure/react-icons';
import { globalMessages } from '../../lib/messages';
import {
  CURRENT_USER_QUERY,
  useCurrentUser
} from '../../lib/core/useCurrentUser';
import { useMutation } from '@apollo/client';
import {
  DISABLE_SUBSCRIPTION_MUTATION,
  UPDATE_PRODUCT_RENEWAL_MUTATION
} from './graphql';
import styled, { css } from 'styled-components';
import { useToasts } from '../../components/Toasts';

const StyledCTA = styled(Button)`
  ${({ theme }) => {
    return css`
      border-color: white;
      @media screen and (max-width: ${theme.breakpoints[2]}) {
        margin: 0 ${theme.space[3]}px 0 0;
      }
      &:hover,
      &:focus {
        border-color: white;
        color: ${theme.colors.primary};
      }
    `;
  }};
`;

const StyledButton = styled(Button)`
  ${({ theme }) => {
    return css`
      @media screen and (max-width: ${theme.breakpoints[2]}) {
        margin: 0 ${theme.space[3]}px 0 0;
      }
    `;
  }};
`;

function SubscriptionDescription({ currentSubscription }) {
  const intl = useIntl();
  if (!currentSubscription || !currentSubscription.product) {
    return null;
  }
  const isRelative = currentSubscription.product.durationMethod === 'relative';

  if (isRelative && currentSubscription.product.durationMins) {
    if (currentSubscription.state === 'pending') {
      return (
        <>
          <Text color="primaryContrast">
            <Text color="primaryContrast">
              <FormattedMessage
                id="modules.Profile.CurrentSubscription.validFor"
                defaultMessage="Lasts for {durationMessage}"
                values={{
                  durationMessage: formatDuration({
                    intl,
                    durationMins: currentSubscription.product.durationMins
                  })
                }}
              />
            </Text>
            <FormattedMessage
              id="modules.Profile.CurrentSubscription.relative.pending.validFrom"
              defaultMessage="Starts when you unlock a bike"
            />
          </Text>
        </>
      );
    } else if (
      currentSubscription.state === 'active' &&
      currentSubscription.validFrom
    ) {
      return (
        <>
          <Text color="primaryContrast">
            <Text color="primaryContrast">
              <FormattedMessage
                id="modules.Profile.CurrentSubscription.validFor"
                defaultMessage="Lasts for {durationMessage}"
                values={{
                  durationMessage: formatDuration({
                    intl,
                    durationMins: currentSubscription.product.durationMins
                  })
                }}
              />
            </Text>
            <FormattedMessage
              id="modules.Profile.CurrentSubscription.relative.active.validFrom"
              defaultMessage="Started {createdAt}"
              values={{
                createdAt: (
                  <FormattedDate
                    value={new Date(currentSubscription.validFrom)}
                    format="date-time"
                  />
                )
              }}
            />
          </Text>
        </>
      );
    }
  }
  return (
    <>
      {currentSubscription.createdAt && (
        <Text color="primaryContrast">
          <FormattedMessage
            id="from_time"
            defaultMessage="From {createdAt}"
            values={{
              createdAt: (
                <FormattedDate
                  value={new Date(currentSubscription.createdAt)}
                  format="date-time"
                />
              )
            }}
          />
        </Text>
      )}
      {currentSubscription.validTo && (
        <Text color="primaryContrast">
          <FormattedMessage
            id="modules.Profile.CurrentSubscription.validTo"
            defaultMessage="Until {validTo}"
            values={{
              validTo: (
                <FormattedDate
                  value={new Date(currentSubscription.validTo)}
                  format="date-time"
                />
              )
            }}
          />
        </Text>
      )}
    </>
  );
}

const RenewableSubscription = ({
  productRenewals,
  currentSubscription
}: {
  productRenewals: CurrentUser_productRenewals[];
  currentSubscription: CurrentUser_currentSubscription | null;
}) => {
  const intl = useIntl();
  const [updateProductRenewal, { loading, error, data }] = useMutation<
    updateProductSubscriptionMutation,
    updateProductSubscriptionMutationVariables
  >(UPDATE_PRODUCT_RENEWAL_MUTATION, {
    onError: () => {
      // error is handled in the UI
    },
    update: (cache, { data }) => {
      if (!data) {
        return;
      }
      const { updateProductRenewal } = data;
      const currentUserResult = cache.readQuery<currentUserQuery>({
        query: CURRENT_USER_QUERY
      });
      if (!currentUserResult) {
        return;
      }
      switch (updateProductRenewal.__typename) {
        case 'UpdateProductRenewalError': {
          if (
            updateProductRenewal.errorCode ===
            UpdateProductRenewalErrorCode.ERR_USER_NO_SUBSCRIPTION
          ) {
            // remove subscription from user if this happens
            cache.writeQuery<currentUserQuery>({
              query: CURRENT_USER_QUERY,
              data: {
                ...currentUserResult,
                currentUser: currentUserResult.currentUser
                  ? {
                      ...currentUserResult.currentUser,
                      currentSubscription: null
                    }
                  : null
              }
            });
          }
          break;
        }
        case 'User': {
          cache.writeQuery<currentUserQuery>({
            query: CURRENT_USER_QUERY,
            data: {
              ...currentUserResult,
              currentUser: currentUserResult.currentUser
                ? {
                    ...currentUserResult.currentUser,
                    ...updateProductRenewal
                  }
                : null
            }
          });
          break;
        }
      }
    }
  });

  let component: React.ReactElement | null = null;
  const productRenewal = productRenewals[0];
  if (!productRenewal) {
    const currentSubscriptionProduct =
      currentSubscription && currentSubscription.product;

    if (
      !currentSubscription ||
      !currentSubscriptionProduct ||
      !currentSubscriptionProduct.isRenewable
    ) {
      return null;
    }

    const price = currentSubscriptionProduct.price || 0;

    component = (
      <>
        <AnchorButton
          color="primaryContrast"
          onClick={() => {
            const confirmMessage = `${intl.formatMessage(
              messages.renewalMessages.confirmRenew
            )} ${
              currentSubscription.validTo
                ? intl.formatMessage(
                    messages.renewalMessages.subscriptionRenewalOnDate,
                    {
                      price: formatCurrency({ value: price, intl }),
                      date: intl.formatDate(currentSubscription.validTo, {
                        format: 'date'
                      })
                    }
                  )
                : ''
            }`;
            if (confirm(confirmMessage)) {
              updateProductRenewal({
                variables: {
                  productId: currentSubscriptionProduct.id,
                  isEnabled: true
                }
              });
            }
          }}
        >
          <FormattedMessage
            id="renew_automatically"
            defaultMessage="Renew automatically"
          />
        </AnchorButton>
      </>
    );
  } else {
    switch (productRenewal.__typename) {
      case 'ProductRenewalScheduled': {
        const price = productRenewal.product.price || 0;
        component = (
          <FormattedMessage
            {...messages.renewalMessages.subscriptionRenewalOnDate}
            values={{
              price: <FormattedCurrency value={price} />,
              date: (
                <FormattedDate
                  value={productRenewal.scheduledAt}
                  format="date"
                />
              )
            }}
          />
        );
        break;
      }
      case 'ProductRenewalPending': {
        component = (
          <FormattedMessage
            id="subscription_renewal_scheduled_after_activation"
            defaultMessage="Renewal will be scheduled after subscription activation."
          />
        );
        break;
      }
      case 'ProductRenewalFailed': {
        component = (
          <>
            <Text>
              <FormattedMessage
                id="renewal_failed"
                defaultMessage="Automatic renewal for {productName} failed."
                values={{
                  productName: productRenewal.product.name
                }}
              />
            </Text>
          </>
        );
        break;
      }
    }
  }

  if (!component) {
    return null;
  }

  return (
    <Box mt="xxs" data-testid="RenewableSubscription">
      {!productRenewal && (
        <Text mb={2}>
          <FormattedMessage
            id="renewal_not_enabled"
            defaultMessage="Automatic renewal is not enabled."
          />
        </Text>
      )}
      <AnimatePresence initial={false}>
        {component && (
          <motion.div
            key="component"
            initial={{ y: -50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -50, opacity: 0, transition: { duration: 0.2 } }}
          >
            {component}
          </motion.div>
        )}
        {loading && (
          <motion.div
            key="loading"
            initial={{ y: -50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -50, opacity: 0, transition: { duration: 0.2 } }}
            style={{ position: 'absolute' }}
          >
            <Spinner ml={2} />
          </motion.div>
        )}
      </AnimatePresence>
      {productRenewal && (
        <AnchorButton
          color="white"
          mt={2}
          onClick={() => {
            let confirmMessage = intl.formatMessage(
              messages.renewalMessages.confirmCancel
            );

            if (currentSubscription) {
              if (
                currentSubscription.state === ProductSubscriptionStatus.pending
              ) {
                confirmMessage += ` ${intl.formatMessage(
                  messages.renewalMessages.subscriptionStillPending
                )}`;
              } else if (
                currentSubscription.state ===
                  ProductSubscriptionStatus.active &&
                currentSubscription.validTo
              ) {
                confirmMessage += ` ${intl.formatMessage(
                  messages.renewalMessages.confirmCancelWithValidSubscription,
                  {
                    validTo: intl.formatDate(currentSubscription.validTo)
                  }
                )}`;
              }
            }

            if (confirm(confirmMessage)) {
              updateProductRenewal({
                variables: {
                  productId: productRenewal.product.id,
                  isEnabled: false
                }
              });
            }
          }}
        >
          <FormattedMessage
            id="renewal_cancel"
            defaultMessage="Cancel automatic renewal"
          />
        </AnchorButton>
      )}
      {data &&
        data.updateProductRenewal.__typename ===
          'UpdateProductRenewalError' && (
          <Flex mt="xxs" flexDirection="column" alignItems="center">
            <Warning color="white" mb={1} />
            <FormattedMessage
              {...messages.getUpdateProductRenewalErrorMessage(
                data.updateProductRenewal
              )}
            />
          </Flex>
        )}
      {error && (
        <Flex mt="xxs" flexDirection="column" alignItems="center">
          <Warning color="white" mb={1} />
          <FormattedMessage {...globalMessages.genericError} />
        </Flex>
      )}
    </Box>
  );
};

export const CurrentSubscription = () => {
  const { addToast } = useToasts();
  const { currentUser } = useCurrentUser();

  const [disableSubscription] = useMutation<
    disableSubscription,
    disableSubscriptionVariables
  >(DISABLE_SUBSCRIPTION_MUTATION, {
    variables: {
      subscriptionId: currentUser?.currentSubscription?.id as string
    },
    onCompleted: () => {
      addToast({
        text: (
          <FormattedMessage
            {...messages.profileSection.cancelSubscriptionSuccess}
          />
        ),
        type: 'success'
      });
    },
    update: (cache, { data }) => {
      if (!data) {
        return;
      }
      const currentUserResult = cache.readQuery<currentUserQuery>({
        query: CURRENT_USER_QUERY
      });
      if (!currentUserResult) {
        return;
      }
      cache.writeQuery<currentUserQuery>({
        query: CURRENT_USER_QUERY,
        data: {
          ...currentUserResult,
          currentUser: currentUserResult.currentUser
            ? {
                ...currentUserResult.currentUser,
                currentSubscription: null
              }
            : null
        }
      });
    },
    onError: () => {
      addToast({
        text: <FormattedMessage {...globalMessages.genericError} />,
        type: 'error'
      });
    }
  });

  if (!currentUser) {
    return null;
  }

  const { currentSubscription } = currentUser;
  const freeSubscription = currentSubscription?.product?.price === 0;
  const isNotOnTrip = currentUser?.currentTrip === null;
  const paymentCardIsRemovable =
    currentUser?.paymentMethod?.__typename === 'Card' &&
    currentUser?.paymentMethod?.removable;

  return (
    <Box>
      <Flex justifyContent="center">
        <ProductCard mb={7} maxWidth={450} data-testid="CurrentSubscription">
          <Text mb="xxs" typoStyle="xxs">
            <FormattedMessage
              id="modules.Profile.CurrentSubscription.heading"
              defaultMessage="Your subscription"
            />
          </Text>
          {!currentSubscription && (
            <>
              <Text as="p">
                <FormattedMessage
                  {...messages.profileSection.withoutSubscription}
                />
              </Text>
              <Flex flexDirection="column">
                {currentUser.penaltyStatus === PenaltyStatus.blocked ? (
                  <Dialog
                    heading={
                      <FormattedMessage
                        id="profile.penalties.blocked.title"
                        defaultMessage="Account suspended"
                      />
                    }
                    size="medium"
                    showCloseButton={true}
                    renderOpenButton={openPortal => (
                      <Box>
                        <StyledCTA
                          variant="secondary"
                          shape="cool"
                          onClick={openPortal}
                        >
                          <I18NBuy />
                        </StyledCTA>
                      </Box>
                    )}
                  >
                    {({ closePortal }) => {
                      return (
                        <React.Fragment>
                          <Flex mb={6}>
                            <FormattedMessage
                              id="profile.penalties.blocked.purchase_warning"
                              defaultMessage="You will not be able to use the subscription you are about to purchase."
                            />
                          </Flex>
                          <Flex>
                            <ButtonGroup>
                              <StyledButton
                                variant="secondary"
                                shape="cool"
                                onClick={closePortal}
                              >
                                <I18NCancel />
                              </StyledButton>
                              <Link route="buy" passHref>
                                <StyledButton variant="primary" shape="cool">
                                  <I18NContinue />
                                </StyledButton>
                              </Link>
                            </ButtonGroup>
                          </Flex>
                        </React.Fragment>
                      );
                    }}
                  </Dialog>
                ) : (
                  <>
                    {' '}
                    <Box>
                      <Link route="buy" passHref>
                        <ButtonAnchor variant="inverted" shape="cool">
                          <I18NBuy />
                        </ButtonAnchor>
                      </Link>
                    </Box>
                  </>
                )}
                <Box>
                  <Link route="redeem-coupon" passHref>
                    <ButtonAnchor
                      shape="cool"
                      variant="link"
                      color="primaryContrast"
                    >
                      <FormattedMessage
                        {...messages.profileSection.redeemGiftCardLink}
                      />
                    </ButtonAnchor>
                  </Link>
                </Box>
              </Flex>
            </>
          )}
          {currentSubscription && currentSubscription.product && (
            <>
              <H2
                as="div"
                mb={4}
                data-testid="Subscription__CurrentSubscription__ProductName"
              >
                {currentSubscription.product.name}
              </H2>
              <SubscriptionDescription
                currentSubscription={currentSubscription}
              />
              {freeSubscription && isNotOnTrip && paymentCardIsRemovable && (
                <Box>
                  <Dialog
                    heading={
                      <FormattedMessage
                        {...messages.profileSection.cancelSubscriptionTitle}
                      />
                    }
                    size="medium"
                    showCloseButton={true}
                    renderOpenButton={openPortal => (
                      <StyledButton
                        variant="link"
                        shape="cool"
                        color={'#fff'}
                        onClick={openPortal}
                      >
                        <FormattedMessage
                          {...messages.profileSection.cancelSubscription}
                        />
                      </StyledButton>
                    )}
                  >
                    {({ closePortal }) => {
                      return (
                        <React.Fragment>
                          <Flex mb={6}>
                            <FormattedMessage
                              {...messages.profileSection
                                .cancelSubscriptionConfirmation}
                            />
                          </Flex>
                          <Flex>
                            <ButtonGroup>
                              <StyledButton
                                variant="secondary"
                                shape="cool"
                                onClick={closePortal}
                              >
                                <I18NCancel />
                              </StyledButton>
                              <Link route="profile" passHref>
                                <StyledButton
                                  variant="primary"
                                  shape="cool"
                                  onClick={() => {
                                    disableSubscription({
                                      variables: {
                                        subscriptionId: currentUser
                                          ?.currentSubscription?.id as string
                                      }
                                    });
                                    closePortal();
                                  }}
                                >
                                  <I18NContinue />
                                </StyledButton>
                              </Link>
                            </ButtonGroup>
                          </Flex>
                        </React.Fragment>
                      );
                    }}
                  </Dialog>
                </Box>
              )}
            </>
          )}
          <RenewableSubscription
            currentSubscription={currentSubscription}
            productRenewals={currentUser.productRenewals}
          />
        </ProductCard>
      </Flex>
    </Box>
  );
};
